import { EXTERNAL_ROUTES } from '@/constants';
import { CourseDetailStrip } from '@/modules/applications/components/ListApplicationsTable/CourseDetailStrip';
import { CourseCohortDetailsTooltip } from '@/modules/applications/components/ListApplicationsTable/components/CourseCohortDetailsTooltip';
import {
  formatCurrencyValue,
  formatSlug,
} from '@/modules/common/lib/formatters';
import { ICompany } from '@/types/company';
import { PaymentStatus } from '@/types/payment';
import { IUser, UserRole } from '@/types/user';
import { MoreHoriz, OpenInNew } from '@mui/icons-material';
import { Button, Chip, Menu, MenuItem, Typography } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { format, isValid } from 'date-fns';
import { MRT_ColumnDef } from 'material-react-table';
import { useState } from 'react';
import { PaymentPageStage } from '../../types';
import { PaymentStatusChip } from '../PaymentStatusChip';
import { PaymentInTheTable } from './ListPayments.types';

type GetListPaymentsTableColumnsArgs = {
  onPay: (rowId: string) => void;
  userRoles: UserRole[];
  stage?: PaymentPageStage;
  hideActions?: boolean;
};

export function getListPaymentsTableTableColumns({
  onPay,
  userRoles,
  stage,
  hideActions,
}: GetListPaymentsTableColumnsArgs): MRT_ColumnDef<PaymentInTheTable>[] {
  const columns: MRT_ColumnDef<PaymentInTheTable>[] = [];

  columns.push({
    accessorKey: 'totalAmount',
    header: 'Amount',
    Cell: ({ row }) => (
      <div className="flex gap-2 items-center">
        <Typography variant="subtitle2">
          {formatCurrencyValue(
            row.original.totalAmount.toString(),
            row.original.currency,
          )}
        </Typography>
        <PaymentStatusChip status={row.original.status} />
      </div>
    ),
  });

  if (userRoles.includes(UserRole.TaxBenefitsProvider)) {
    columns.push({
      accessorKey: 'user.company.name',
      header: 'Company',
      Cell: ({ row }) => (
        <div>
          <Typography variant="caption">
            {((row.original.user as IUser)?.company as ICompany)?.name ?? ''}
          </Typography>
        </div>
      ),
    });
  }

  columns.push({
    accessorKey: 'order.item.course.name',
    header: 'Course Details',
    Cell: ({ row }) => {
      if (!row.original.order) {
        return null;
      }

      const { course, cohort: cohortId } = row.original.order.item;
      const cohort = course.cohorts?.find(cohort => cohort._id === cohortId);

      if (!cohort) {
        return null;
      }

      return (
        <CourseCohortDetailsTooltip course={course} cohort={cohort}>
          <div className="flex flex-col gap-1 w-full break-words text-wrap">
            <Typography
              className="text-mydra-purple font-semibold"
              variant="caption"
            >
              <a
                className="cursor-pointer hover:underline"
                href={EXTERNAL_ROUTES.MARKETPLACE_COURSE_URL.replace(
                  ':id',
                  course._id!,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {course?.name}
              </a>
            </Typography>
            <Typography className="text-xs" variant="caption">
              {course?.educationProvider?.name}
            </Typography>
            <CourseDetailStrip course={course} cohort={cohort} />
          </div>
        </CourseCohortDetailsTooltip>
      );
    },
  });

  columns.push({
    id: 'user',
    accessorKey: 'user',
    header: 'Employee',
    Cell: ({ row }) => (
      <div className="flex items-center gap-3 w-full break-words text-wrap">
        <div className="flex flex-col w-full">
          <div className="text-sm font-medium">
            <Link
              to={`/employees/${(row.original.user as IUser)._id}`}
            >{`${(row.original.user as IUser)?.firstName} ${(row.original.user as IUser)?.lastName}`}</Link>
          </div>
          <div className="text-xxs text-text">
            {(row.original.user as IUser).email}
          </div>
        </div>
      </div>
    ),
  });

  columns.push({
    accessorKey: 'paymentResource',
    header: 'Type',
    Cell: ({ row }) => (
      <div className="flex flex-col flex-1 items-start gap-2">
        <Chip
          label={row.original.paymentResource?.replace('_', ' ') || 'N/A'}
          className="capitalize"
          size="small"
        />
      </div>
    ),
  });

  columns.push({
    accessorKey: 'createdAt',
    header: 'Date',
    Cell: ({ row }) => (
      <div>
        <Typography>
          {isValid(new Date(row.original.createdAt)) &&
            format(row.original.createdAt, 'Pp')}
        </Typography>
      </div>
    ),
  });

  columns.push({
    accessorKey: 'status',
    header: 'Status',
    filterVariant: 'select',
    filterSelectOptions: Object.values(PaymentStatus).map(status => ({
      text: formatSlug(status.toLowerCase()),
      value: status,
    })),
    Cell: ({ row }) => <PaymentStatusChip status={row.original.status} />,
  });

  columns.push({
    accessorKey: 'order.item.course.educationProvider._id',
    id: 'educationProvider',
    header: 'Education Provider',
    filterVariant: 'multi-select',
    Cell: () => null, // Hidden column for filtering only
  });

  // Only add actions column if not hidden
  if (!hideActions) {
    columns.push({
      id: 'actions',
      header: 'Actions',
      muiTableHeadCellProps: {
        align: 'right',
      },
      muiTableBodyCellProps: {
        align: 'right',
      },
      Cell({ row }) {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const showPayButton = stage === PaymentPageStage.AWAITING_PAYMENT;

        return showPayButton ? (
          <Button
            disabled={row.original.status !== PaymentStatus.Pending}
            onClick={() => {
              onPay(row.id);
            }}
            startIcon={<OpenInNew />}
          >
            Pay now
          </Button>
        ) : (
          <>
            <Button
              variant="text"
              startIcon={<MoreHoriz />}
              disabled={row.original.status !== PaymentStatus.Pending}
              onClick={e => {
                setAnchorEl(e.currentTarget);
              }}
            />
            <Menu
              open={!!anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorEl={anchorEl}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onPay(row.id);
                }}
              >
                <div className="flex items-center gap-2">
                  <OpenInNew />
                  Pay now
                </div>
              </MenuItem>
            </Menu>
          </>
        );
      },
    });
  }

  return columns;
}
